// client/src/components/pages/LexiStar/Dashboard/TopExceptions.tsx
//
// "Top five things to look at" (LS-09 §5.15, LS-03 FR-RPT-001 "top
// exceptions"; m4-contracts §4 GET /kpis/top-exceptions?date). The API ranks
// open deviations, fuel overruns, mass-variance breaches, tickets without
// presence and review items by impact; this list shows the first five in that
// order, each opening the place where it is dealt with. Event severity may
// appear on these rows (LS-09 §4: severity is for chips and list rows).

import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import {
  FaRoute,
  FaGasPump,
  FaBalanceScale,
  FaReceipt,
  FaTasks,
  FaHourglassHalf,
  FaExclamationCircle,
  FaChevronRight,
} from 'react-icons/fa';
import { SeverityChip as SeverityChipJs } from '../StatusBoard/atoms';
import { SkeletonBlock, cardClasses } from './analyticsAtoms';
import type { ExceptionKind, TopException } from './kpiModel';

const SeverityChip = SeverityChipJs as unknown as ComponentType<{ severity: string }>;

const KIND_META: Record<ExceptionKind, { label: string; Icon: ComponentType<{ size?: number; className?: string }>; fallback: string }> = {
  deviation: { label: 'Open deviation', Icon: FaRoute, fallback: '/lexistar/approvals' },
  fuel_overrun: { label: 'Fuel overrun', Icon: FaGasPump, fallback: '/lexistar/kpis?tab=route' },
  mass_variance: { label: 'Mass variance', Icon: FaBalanceScale, fallback: '/lexistar/review' },
  ticket_without_presence: { label: 'Ticket without presence', Icon: FaReceipt, fallback: '/lexistar/review' },
  review_item: { label: 'Review item', Icon: FaTasks, fallback: '/lexistar/review' },
  dwell_exceeded: { label: 'Dwell over limit', Icon: FaHourglassHalf, fallback: '/lexistar/dwell' },
  other: { label: 'Exception', Icon: FaExclamationCircle, fallback: '/lexistar/review' },
};

export default function TopExceptions({
  items,
  loading,
  error,
  dayText,
}: {
  items: TopException[];
  loading: boolean;
  error: string | null;
  dayText: string;
}) {
  const top = items.slice(0, 5);
  return (
    <section className={`${cardClasses} p-4 flex flex-col`} aria-labelledby="top-five-title">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h2 id="top-five-title" className="text-sm font-semibold text-slate-900">
          Top five things to look at
        </h2>
        <span className="text-xs text-slate-500 tabular-nums">{dayText}</span>
      </div>
      {error ? (
        <p className="text-sm text-slate-600 py-4" role="alert">
          {error}
        </p>
      ) : loading && !items.length ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 5 }, (_, i) => (
            <SkeletonBlock key={i} className="h-14" />
          ))}
        </div>
      ) : !top.length ? (
        <div className="py-6 text-center">
          <p className="text-sm font-medium text-slate-900">Nothing stands out</p>
          <p className="text-xs text-slate-600 mt-1 max-w-xs mx-auto">
            No open deviations, fuel overruns, mass-variance breaches, tickets without presence or review items for this
            day.
          </p>
        </div>
      ) : (
        <ol className="-mx-1.5 divide-y divide-slate-100">
          {top.map((x, i) => {
            const meta = KIND_META[x.kind];
            return (
              <li key={x.id}>
                <Link
                  to={x.drill || meta.fallback}
                  className="group flex items-start gap-3 rounded-lg px-1.5 py-2.5 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <span className="w-5 flex-shrink-0 text-lg font-semibold leading-6 text-slate-400 tabular-nums text-center" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                      <meta.Icon size={10} className="flex-shrink-0" aria-hidden="true" />
                      {meta.label}
                      {x.severity && <SeverityChip severity={x.severity} />}
                    </span>
                    <span className="block text-sm font-medium text-slate-900 leading-snug mt-0.5">{x.title}</span>
                    {(x.detail || x.subject) && (
                      <span className="block text-xs text-slate-600 mt-0.5">
                        {[x.subject, x.detail].filter(Boolean).join(' — ')}
                      </span>
                    )}
                  </span>
                  {x.figure && (
                    <span className="flex-shrink-0 text-sm font-semibold text-slate-900 tabular-nums text-right leading-6">{x.figure}</span>
                  )}
                  <FaChevronRight size={10} className="flex-shrink-0 mt-2 text-slate-300 group-hover:text-slate-500" aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
